import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  useDeleteUserStandardMutation,
  useUpdateUserStandardMutation,
} from '../../standardsCatalogApi';
import type { UserStandardTreeNode } from '../../utils/userStandardsTree';
import { CodeChip, EmptyState, StatusBadge } from '../atoms';

import type { UUID } from '@/shared/types/userStandard';
import { useConfirm } from '@/shared/components/ConfirmProvider';

interface LibraryDetailsPanelProps {
  node: UserStandardTreeNode | null;
  ownerId: UUID;
  onAddChild: (parentId: UUID, type: 'folder' | 'standard') => void;
  onMove: (node: UserStandardTreeNode) => void;
  onAfterDelete: () => void;
  onError: (message: string) => void;
  onClose?: () => void;
}

function provenanceString(node: UserStandardTreeNode): string {
  const ac = node.additional_content ?? {};
  return [ac.jurisdictionTitle, ac.standardSetTitle].filter(Boolean).map(String).join(' · ');
}

export function LibraryDetailsPanel({
  node,
  ownerId,
  onAddChild,
  onMove,
  onAfterDelete,
  onError,
  onClose,
}: LibraryDetailsPanelProps) {
  const confirm = useConfirm();
  const [updateNode, { isLoading: saving }] = useUpdateUserStandardMutation();
  const [deleteNode] = useDeleteUserStandardMutation();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [statement, setStatement] = useState('');

  const isFolder = node?.node_type === 'folder';
  const isImported = Boolean(node?.csp_standard_id);

  useEffect(() => {
    setName(node?.title ?? '');
    setCode(node?.code ?? '');
    setStatement(node?.title ?? '');
  }, [node?.id, node?.title, node?.code]);

  if (!node) {
    return (
      <EmptyState
        icon={<DescriptionOutlinedIcon />}
        title="Select an item"
        description="Pick a folder or standard on the left to view and edit its details."
      />
    );
  }

  const dirty = isFolder
    ? name.trim() !== node.title
    : code.trim() !== (node.code ?? '') || statement.trim() !== node.title;

  const discard = () => {
    setName(node.title);
    setCode(node.code ?? '');
    setStatement(node.title);
  };

  const save = async () => {
    try {
      if (isFolder) {
        await updateNode({
          id: node.id,
          title: name.trim() || node.title,
          parentId: node.parent_id,
          ownerId,
        }).unwrap();
      } else {
        const text = statement.trim();
        await updateNode({
          id: node.id,
          code: code.trim() || null,
          title: text || node.title,
          description: text || null,
          parentId: node.parent_id,
          ownerId,
        }).unwrap();
      }
    } catch {
      discard();
      onError('Could not save your changes. Please try again.');
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: isFolder ? 'Delete folder?' : 'Delete standard?',
      message: isFolder
        ? 'This deletes the folder and everything inside it.'
        : 'This moves the standard to trash.',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!ok) return;
    try {
      await deleteNode({ id: node.id, parentId: node.parent_id, ownerId }).unwrap();
      onAfterDelete();
    } catch {
      onError('Could not delete this item. Please try again.');
    }
  };

  const provenance = provenanceString(node);
  const documentUrl = node.additional_content?.documentUrl;
  const rawComments = node.additional_content?.comments;
  const comments = Array.isArray(rawComments)
    ? rawComments.filter((c): c is string => typeof c === 'string')
    : [];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {isFolder ? (
          <FolderOutlinedIcon fontSize="small" color="action" />
        ) : (
          <DescriptionOutlinedIcon fontSize="small" color="action" />
        )}
        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>
          {isFolder ? 'Folder' : 'Standard'}
        </Typography>
        {!isFolder && <StatusBadge variant={isImported ? 'imported' : 'custom'} />}
        {onClose && (
          <Tooltip title="Close details">
            <IconButton size="small" onClick={onClose} aria-label="Close details">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        <Stack spacing={2}>
          {isFolder ? (
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          ) : (
            <>
              <TextField
                label="Code"
                size="small"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                sx={{ maxWidth: 240 }}
              />
              <TextField
                label="Statement"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={statement}
                onChange={(event) => setStatement(event.target.value)}
              />
            </>
          )}

          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button size="small" color="inherit" onClick={discard} disabled={!dirty || saving}>
              Discard
            </Button>
          </Stack>

          {isImported && provenance && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
              <Typography variant="overline" color="text.secondary">
                Source
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                {node.code && <CodeChip code={node.code} />}
                <Typography variant="body2">Imported from {provenance}</Typography>
              </Stack>
              {typeof documentUrl === 'string' && documentUrl && (
                <Link
                  href={documentUrl}
                  target="_blank"
                  rel="noopener"
                  variant="caption"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1 }}
                >
                  <OpenInNewIcon sx={{ fontSize: 14 }} /> View in source
                </Link>
              )}
            </Box>
          )}

          {!isFolder && comments.length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Notes
              </Typography>
              {/* listStyleType is explicit because Preflight sets `list-style: none`
                  on every list; without it these notes lose their bullets. */}
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3, listStyleType: 'disc' }}>
                {comments.map((comment, index) => (
                  <Typography key={index} component="li" variant="body2" color="text.secondary">
                    {comment}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Divider />

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {/* Any node can be a parent — standards can nest standards or folders under them,
                same as folders (parity with Find Standards). */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<NoteAddOutlinedIcon />}
              onClick={() => onAddChild(node.id, 'standard')}
            >
              Add standard
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={() => onAddChild(node.id, 'folder')}
            >
              Add folder
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DriveFileMoveOutlinedIcon />}
              onClick={() => onMove(node)}
            >
              Move to…
            </Button>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Delete">
              <IconButton color="error" onClick={handleDelete} aria-label="Delete">
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

export default LibraryDetailsPanel;
